import { createComposerFrameRenderer } from './share-composer-frame-cache';
import { drawComposerFrame } from './share-composer-render';
import { composerDimensions, COMPOSER_QUALITIES, prepareComposerFonts } from './share-composer-model';
import { gifAnimation } from './share-gif-animation';

export function videoFormat(Recorder = window.MediaRecorder, sound = false) {
  if (!Recorder) return null;
  const formats = sound ? [['video/mp4;codecs=avc1.640033,mp4a.40.2', 'mp4'], ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'mp4'], ['video/webm;codecs=vp9,opus', 'webm'], ['video/webm;codecs=vp8,opus', 'webm']] : [['video/mp4;codecs=avc1.640033', 'mp4'], ['video/mp4;codecs=avc1.42E01E', 'mp4'], ['video/mp4', 'mp4'], ['video/webm;codecs=vp9', 'webm'], ['video/webm;codecs=vp8', 'webm'], ['video/webm', 'webm']];
  for (const [mime, extension] of formats) {
    if (Recorder.isTypeSupported?.(mime)) return { mime, extension };
  }
  return null;
}
export { downloadExportBlob as saveComposerBlob } from './share-file-delivery';
const aborted = () => new DOMException('Export cancelled.', 'AbortError');
function check(signal) { if (signal?.aborted) throw aborted(); }
export async function loadComposerGIF(file, signal) {
  const { createGIFDecoder } = await import('./share-gif-worker-factory'); check(signal);
  const buffer = await file.arrayBuffer(); check(signal); const worker = createGIFDecoder();
  try { const decoded = await workerRequest(worker, buffer, [buffer], signal); if (decoded.error) throw new Error(decoded.error); return gifAnimation(decoded); }
  finally { worker.terminate(); }
}
export async function loadComposerImage(src) {
  if (!src) return null;
  return new Promise((resolve, reject) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => { img.src = ''; reject(new Error('An image took too long to load. Try uploading it again.')); }, 12000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); reject(new Error('An image could not be loaded. Remove it or choose another image.')); };
    img.src = src;
  });
}
export async function seekComposerVideo(video, time, signal) {
  check(signal); if (!video || Math.abs(video.currentTime - time) < .015) return;
  await new Promise((resolve, reject) => {
    const done = () => { cleanup(); resolve(); }, cancel = () => { cleanup(); reject(aborted()); };
    const fail = () => { cleanup(); reject(new Error('The video could not be read at this position.')); };
    const timeout = setTimeout(fail, 5000);
    const cleanup = () => { clearTimeout(timeout); video.removeEventListener('seeked', done); video.removeEventListener('error', fail); signal?.removeEventListener('abort', cancel); };
    video.addEventListener('seeked', done, { once: true }); video.addEventListener('error', fail, { once: true }); signal?.addEventListener('abort', cancel, { once: true }); video.currentTime = time;
  });
}
function makeCanvas(shape, type, quality) {
  const size = composerDimensions(shape, type, quality), canvas = document.createElement('canvas');
  canvas.width = size.width; canvas.height = size.height;
  return canvas;
}
function workerRequest(worker, message, transfer = [], signal) {
  check(signal);
  return new Promise((resolve, reject) => {
    const clear = () => { worker.removeEventListener('message', receive); worker.removeEventListener('error', failed); signal?.removeEventListener('abort', cancel); clearTimeout(timeout); };
    const receive = event => { clear(); event.data.type === 'error' ? reject(new Error(event.data.message)) : resolve(event.data); };
    const failed = () => { clear(); reject(new Error('The GIF encoder is unavailable. Please use Photo or Video.')); };
    const cancel = () => { clear(); reject(aborted()); }, timeout = setTimeout(failed, 15000);
    worker.addEventListener('message', receive); worker.addEventListener('error', failed); signal?.addEventListener('abort', cancel, { once: true }); worker.postMessage(message, transfer);
  });
}

export async function exportComposer(input) {
  check(input.signal);
  if (input.type === 'video' && typeof window.VideoEncoder === 'function') {
    const { exportSmoothVideo } = await import('./share-video-export');
    return exportSmoothVideo(input);
  }
  let audio, audioReady;
  if (input.type === 'video' && input.render.media?.tagName === 'VIDEO' && input.sound > 0) {
    const Audio = window.AudioContext || window.webkitAudioContext;
    if (!Audio) throw new Error('This browser cannot include clip audio. Set sound to zero or use another browser.');
    // Safari needs the context activated inside the download click, before
    // asynchronous font loading, video decoding or seeking consumes the gesture.
    audio = new Audio(); audioReady = audio.resume().then(() => null, error => error);
  }
  try { return await renderComposerExport({ ...input, audio, audioReady }); }
  finally { if (audio && audio.state !== 'closed') await audio.close().catch(() => {}); }
}
async function renderComposerExport({ type, shape, quality = 'high', render, trimStart = 0, duration = 6, sound = 0, signal, onProgress = () => {}, audio, audioReady }) {
  check(signal); await prepareComposerFonts(render.design, render.theme); check(signal);
  let video = render.media?.tagName === 'VIDEO' ? render.media : null, exportVideo;
  if (type === 'video' && video) {
    exportVideo = document.createElement('video'); exportVideo.muted = true; exportVideo.playsInline = true; exportVideo.preload = 'auto'; exportVideo.src = video.currentSrc || video.src;
    await new Promise((resolve, reject) => {
      const done = () => { cleanup(); resolve(); }, cancel = () => { cleanup(); reject(aborted()); }, fail = () => { cleanup(); reject(new Error('The video could not be prepared for export.')); };
      const timeout = setTimeout(fail, 10000), cleanup = () => { clearTimeout(timeout); exportVideo.removeEventListener('loadeddata', done); exportVideo.removeEventListener('error', fail); signal?.removeEventListener('abort', cancel); };
      exportVideo.addEventListener('loadeddata', done, { once: true }); exportVideo.addEventListener('error', fail, { once: true }); signal?.addEventListener('abort', cancel, { once: true }); exportVideo.load();
    }).catch(error => { exportVideo.removeAttribute('src'); exportVideo.load(); throw error; });
    video = exportVideo;
  }
  const settings = COMPOSER_QUALITIES[quality] || COMPOSER_QUALITIES.high;
  const canvas = makeCanvas(shape, type, quality);
  if (type === 'video') { canvas.width = Math.ceil(canvas.width / 2) * 2; canvas.height = Math.ceil(canvas.height / 2) * 2; }
  const frameOptions = { ...render, ...(exportVideo ? { media: exportVideo } : {}) };
  const renderer = type === 'gif' ? createComposerFrameRenderer(canvas, frameOptions) : null;
  const draw = time => {
    const options = { time, animateHero: type !== 'png', opaque: type === 'video' };
    if (renderer) renderer.draw(options); else drawComposerFrame(canvas, { ...frameOptions, ...options });
  };
  if (type === 'png') {
    draw(render.time || 0); const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png')); check(signal);
    if (!blob) throw new Error('The photo could not be created.');
    return { blob, extension: 'png' };
  }
  if (type === 'gif') {
    let worker, pendingFrame;
    const lifetime = new AbortController(), cancelWorker = () => lifetime.abort();
    signal?.addEventListener('abort', cancelWorker, { once: true });
    const askWorker = (message, transfer = []) => workerRequest(worker, message, transfer, lifetime.signal);
    try {
      const { createGIFWorker } = await import('./share-gif-worker-factory'); check(signal);
      worker = createGIFWorker(); await askWorker({ type: 'start', colours: Object.values(render.theme || {}).filter(value => typeof value === 'string') });
      const fps = settings.gifFPS, frames = Math.ceil(Math.min(duration, 8) * fps);
      // Built-in atmospheres use one palette sampled across the entire loop.
      // Its stable colours prevent stationary text and gradients shimmering.
      // Uploaded footage keeps an adaptive palette for changing scenes.
      if (!video && !render.gifAnimation) {
        const sample = document.createElement('canvas'); sample.width = 256; sample.height = Math.round(256 * canvas.height / canvas.width);
        const ctx = sample.getContext('2d');
        for (let index = 0; index < 6; index++) {
          check(signal); draw(index / 5 * (frames - 1) / fps); ctx.drawImage(canvas, 0, 0, sample.width, sample.height);
          const pixels = ctx.getImageData(0, 0, sample.width, sample.height).data;
          await askWorker({ type: 'sample', pixels: pixels.buffer }, [pixels.buffer]);
        }
        await askWorker({ type: 'palette' });
      }
      for (let index = 0; index < frames; index++) {
        check(signal); const time = index / fps;
        if (video) await seekComposerVideo(video, trimStart + time, signal);
        // Sample exact clip positions and the decoded GIF timeline. Each frame
        // is acknowledged before the next allocation to bound working memory.
        draw(time); const pixels = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
        const delay = (Math.round((index + 1) * 100 / fps) - Math.round(index * 100 / fps)) * 10;
        // Paint the next frame while the worker encodes this one. Keep only one
        // queued frame so memory stays bounded, even on a phone.
        if (pendingFrame) { const error = await pendingFrame; if (error) throw error; }
        pendingFrame = askWorker({ type: 'frame', pixels: pixels.buffer, width: canvas.width, height: canvas.height, delay }, [pixels.buffer]).then(() => null, error => error);
        onProgress((index + 1) / frames * .98);
      }
      if (pendingFrame) { const error = await pendingFrame; if (error) throw error; }
      const { bytes } = await askWorker({ type: 'finish' }); check(signal);
      return { blob: new Blob([bytes], { type: 'image/gif' }), extension: 'gif' };
    } finally { lifetime.abort(); signal?.removeEventListener('abort', cancelWorker); renderer.dispose(); worker?.terminate(); }
  }
  const format = videoFormat(undefined, sound > 0 && !!video);
  if (!format || !canvas.captureStream) { if (exportVideo) { exportVideo.removeAttribute('src'); exportVideo.load(); } throw new Error('Video export is unavailable with these settings. Try sound at zero, GIF or Photo.'); }
  let stream, recorder, frame, destination, source, gain, monitor;
  const previousTime = video?.currentTime, previousMute = video?.muted;
  try {
    if (video) { video.pause(); await seekComposerVideo(video, trimStart, signal); }
    draw(0); stream = canvas.captureStream(30);
    if (video && sound > 0) {
      // Use an export-only element so the preview never acquires a permanent
      // MediaElementSource and repeated exports keep their audio intact.
      const audioError = await audioReady; if (audioError) throw audioError;
      video.muted = false; video.volume = 1;
      source = audio.createMediaElementSource(video); gain = audio.createGain(); gain.gain.value = sound / 100; destination = audio.createMediaStreamDestination();
      source.connect(gain); gain.connect(destination); destination.stream.getAudioTracks().forEach(track => stream.addTrack(track));
      // Keep Safari's realtime audio graph connected to its output without
      // playing the export aloud. The recorder receives the unmuted branch.
      monitor = audio.createGain(); monitor.gain.value = 0; gain.connect(monitor); monitor.connect(audio.destination);
    }
    check(signal); recorder = new MediaRecorder(stream, { mimeType: format.mime, videoBitsPerSecond: settings.bitrate, audioBitsPerSecond: 256000 });
    const chunks = [];
    await new Promise((resolve, reject) => {
      let completed = false, stopped = false;
      const cleanup = () => { signal?.removeEventListener('abort', cancel); document.removeEventListener('visibilitychange', hidden); clearTimeout(timeout); cancelAnimationFrame(frame); };
      const fail = error => { if (stopped) return; stopped = true; cleanup(); if (recorder.state !== 'inactive') recorder.stop(); reject(error); };
      const cancel = () => fail(aborted());
      const hidden = () => { if (document.hidden) fail(new Error('Keep this tab open while the video is recording. Please try again.')); };
      const timeout = setTimeout(() => fail(new Error('The recording timed out. Please try again.')), (duration + 12) * 1000);
      recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      recorder.onerror = () => fail(new Error('Video recording failed. Try High quality or another format, or download a Photo.'));
      recorder.onstop = () => { if (stopped) return; stopped = true; cleanup(); completed ? resolve() : reject(new Error('Recording stopped before the clip finished. Please try again.')); };
      signal?.addEventListener('abort', cancel, { once: true }); document.addEventListener('visibilitychange', hidden);
      recorder.start(250); const started = performance.now();
      const tick = now => {
        const elapsed = Math.min(duration, (now - started) / 1000); draw(elapsed); onProgress(elapsed / duration);
        if (elapsed >= duration) { completed = true; recorder.stop(); } else frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick); if (video) video.play().catch(() => fail(new Error('The uploaded clip could not play. Try another video.')));
    });
    check(signal); const blob = new Blob(chunks, { type: recorder.mimeType || format.mime });
    if (!blob.size) throw new Error('The recording was empty. Please try again.');
    return { blob, extension: format.extension };
  } finally {
    renderer?.dispose();
    cancelAnimationFrame(frame); if (recorder?.state && recorder.state !== 'inactive') recorder.stop();
    stream?.getTracks().forEach(track => track.stop()); source?.disconnect(); gain?.disconnect(); monitor?.disconnect();
    if (video) { video.pause(); video.muted = previousMute; if (Number.isFinite(previousTime)) video.currentTime = previousTime; }
    if (exportVideo) { exportVideo.removeAttribute('src'); exportVideo.load(); }
  }
}
